import { Fragment, type ReactNode } from 'react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';

/** Matches `nostr:npub1…` / `nostr:nprofile1…` (with or without the `nostr:` prefix). */
const NOSTR_MENTION_REGEX =
  /(?:nostr:)?((?:npub1|nprofile1)[0-9a-z]+)/gi;

/** Build a Jumble.social profile URL for an npub. */
function jumbleUrl(npub: string): string {
  return `https://jumble.social/users/${npub}`;
}

/** Renders a single npub/nprofile mention as the user's display name, linked to Jumble. */
function ProfileMention({ pubkey, npub }: { pubkey: string; npub: string }) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const name = meta?.display_name || meta?.name || genUserName(pubkey);

  return (
    <a
      href={jumbleUrl(npub)}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary hover:underline"
    >
      @{name}
    </a>
  );
}

interface ProfileAboutProps {
  about: string;
  className?: string;
}

/**
 * Render a profile's "about" text as plain text, converting any Nostr
 * npub/nprofile mentions into clickable display-name links that open the
 * user's profile on Jumble.social.
 */
export function ProfileAbout({ about, className }: ProfileAboutProps) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  NOSTR_MENTION_REGEX.lastIndex = 0;
  while ((match = NOSTR_MENTION_REGEX.exec(about)) !== null) {
    const [full, identifier] = match;
    const start = match.index;

    // Push any preceding plain text.
    if (start > lastIndex) {
      nodes.push(
        <Fragment key={`t-${key++}`}>{about.slice(lastIndex, start)}</Fragment>,
      );
    }

    // Decode the identifier to a pubkey + canonical npub.
    let pubkey: string | undefined;
    let npub: string | undefined;
    try {
      const decoded = nip19.decode(identifier);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
        npub = identifier;
      } else if (decoded.type === 'nprofile') {
        pubkey = decoded.data.pubkey;
        npub = nip19.npubEncode(pubkey);
      }
    } catch {
      // fall through — render the raw text below
    }

    if (pubkey && npub) {
      nodes.push(<ProfileMention key={`m-${key++}`} pubkey={pubkey} npub={npub} />);
    } else {
      nodes.push(<Fragment key={`r-${key++}`}>{full}</Fragment>);
    }

    lastIndex = start + full.length;
  }

  // Push any remaining plain text.
  if (lastIndex < about.length) {
    nodes.push(<Fragment key={`t-${key++}`}>{about.slice(lastIndex)}</Fragment>);
  }

  return <span className={cn('whitespace-pre-wrap', className)}>{nodes}</span>;
}
